import type { EvidenceVersionRow } from "~/features/evidences/types";

const DIFF_FIELDS = ["properties", "citation", "metadata"] as const;

export type EvidenceDiffField = (typeof DIFF_FIELDS)[number];

export type EvidenceFieldDiff = {
  field: EvidenceDiffField;
  oldValue: unknown;
  newValue: unknown;
};

type DiffableVersion = Pick<EvidenceVersionRow, "id" | EvidenceDiffField>;

const EMPTY_VERSION: Omit<DiffableVersion, "id"> = { properties: {}, citation: {}, metadata: {} };

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

// ChangeRequestItemDiff cannot render a top-level null/undefined, so every empty value becomes `{}`.
const normalize = (value: unknown): unknown => value ?? {};

function isDeepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => isDeepEqual(item, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every((key) => key in b && isDeepEqual(a[key], b[key]));
  }
  return false;
}

function diffFields(current: DiffableVersion, previous: Omit<DiffableVersion, "id">): EvidenceFieldDiff[] {
  return DIFF_FIELDS.flatMap((field) => {
    const oldValue = normalize(previous[field]);
    const newValue = normalize(current[field]);
    return isDeepEqual(oldValue, newValue) ? [] : [{ field, oldValue, newValue }];
  });
}

/**
 * Changed fields per version id, each against the next older version and the oldest against empty;
 * `versions` must be newest first.
 */
export function buildEvidenceVersionDiffs(versions: readonly DiffableVersion[]): Record<string, EvidenceFieldDiff[]> {
  return Object.fromEntries(versions.map((v, i) => [v.id, diffFields(v, versions[i + 1] ?? EMPTY_VERSION)]));
}
